import styled, { keyframes } from "styled-components";
import { COLORS, INDIGO_CSS_VARS } from "../../common/colors";

// ─── Keyframes ─────────────────────────────────────────────────────────────────

export const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

export const kScaleIn = keyframes`
  from { opacity: 0; transform: scale(0.94); }
  to   { opacity: 1; transform: scale(1);    }
`;

export const kFloat = keyframes`
  0%, 100% { transform: translateY(0);   }
  50%       { transform: translateY(-7px); }
`;

export const kGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0   0   rgba(61,107,94,0.0);  }
  50%       { box-shadow: 0 0 24px 6px rgba(61,107,94,0.18); }
`;

export const kSpin = keyframes`
  from { transform: rotate(0deg);   }
  to   { transform: rotate(360deg); }
`;

export const kShimmerMove = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

// ─── Section root ─────────────────────────────────────────────────────────────

export const Section = styled.section`
  /* ── All color tokens now come from colors.js (INDIGO_CSS_VARS) ── */
  ${INDIGO_CSS_VARS}

  --r-sm: 0.65rem;
  --r:    1.1rem;
  --r-lg: 1.7rem;
  --r-xl: 2.4rem;

  --s-xs: 0 1px 4px  rgba(0,0,0,0.05);
  --s-sm: 0 2px 14px rgba(0,0,0,0.07);
  --s:    0 4px 28px rgba(0,0,0,0.10);
  --s-lg: 0 10px 48px rgba(0,0,0,0.13);
  --s-xl: 0 18px 64px rgba(0,0,0,0.17);

  --t:      0.22s ease;
  --t-slow: 0.40s ease;

  position: relative;
  width: 100%;
  overflow: hidden;
  background: linear-gradient(160deg, ${COLORS.bgCool} 0%, ${COLORS.bgWarm} 100%);
  padding-bottom: 6rem;
`;
Section.displayName = "Section";

// ─── Decorative background ────────────────────────────────────────────────────

export const BgBlob = styled.span`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;

  &.b1 {
    width: 560px; height: 560px;
    top: -160px; left: -140px;
    background: radial-gradient(circle, ${COLORS.primary} 0%, transparent 70%);
    opacity: 0.08; filter: blur(72px);
  }
  &.b2 {
    width: 420px; height: 420px;
    bottom: -100px; right: -100px;
    background: radial-gradient(circle, ${COLORS.primaryLight} 0%, transparent 70%);
    opacity: 0.09; filter: blur(62px);
  }
  &.b3 {
    width: 260px; height: 260px;
    top: 45%; left: 45%;
    background: radial-gradient(circle, ${COLORS.accent} 0%, transparent 70%);
    opacity: 0.07; filter: blur(52px);
  }

  @media (max-width: 48em) {
    &.b1 { width: 280px; height: 280px; }
    &.b2 { width: 220px; height: 220px; }
    &.b3 { display: none; }
  }
`;
BgBlob.displayName = "BgBlob";

/* Subtle mesh/grain overlay */
export const BgMesh = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image:
    radial-gradient(ellipse at 20% 20%, ${COLORS.primaryTint} 0%, transparent 50%),
    radial-gradient(ellipse at 80% 80%, ${COLORS.accentTint}  0%, transparent 50%),
    radial-gradient(circle,             ${COLORS.primaryTint} 1px, transparent 1px);
  background-size: 100% 100%, 100% 100%, 28px 28px;
`;
BgMesh.displayName = "BgMesh";

// ─── Inner wrapper ────────────────────────────────────────────────────────────

export const Inner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 48em) { padding: 0 2rem; }
  @media (min-width: 64em) { padding: 0 3rem; }
`;
Inner.displayName = "Inner";

// ─── Header ───────────────────────────────────────────────────────────────────

export const HeaderWrap = styled.header`
  text-align: center;
  padding-top: 5rem;
  margin-bottom: 1rem;

  /* gradient accent bar replaces TitleUnderline */
  &::after {
    content: "";
    display: block;
    width: 56px;
    height: 4px;
    border-radius: 999px;
    background: linear-gradient(90deg, #3d6b4f 0%, #7a9e4e 50%, #c9a84c 100%);
    margin: 1rem auto 0;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFadeUp} 0.7s ease both;
  }
  @media (min-width: 48em) { padding-top: 6rem; }
`;
HeaderWrap.displayName = "HeaderWrap";

export const Eyebrow = styled.span`
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #3d6b4f;
  margin-bottom: 0.75rem;
`;
Eyebrow.displayName = "Eyebrow";

export const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5.5vw, 3.2rem);
  font-weight: 800;
  color: #2e1f0f;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin: 0;

  em {
    font-style: normal;
    background: linear-gradient(135deg, var(--c-accent), var(--c-accent-2));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;
SectionTitle.displayName = "SectionTitle";

/* Kept for JSX compatibility but replaced by HeaderWrap::after */
export const TitleUnderline = styled.div`
  display: none;
`;
TitleUnderline.displayName = "TitleUnderline";

export const SectionDesc = styled.p`
  font-size: clamp(0.84rem, 2.1vw, 0.97rem);
  color: var(--c-text);
  max-width: 620px;
  margin: 0 auto 4rem;
  line-height: 1.88;
  opacity: 0.68;
`;
SectionDesc.displayName = "SectionDesc";

// ─── Stats strip ─────────────────────────────────────────────────────────────

export const StatsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 4rem;

  @media (min-width: 48em) { grid-template-columns: repeat(4, 1fr); }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFadeUp} 0.6s ease both 0.1s;
  }
`;
StatsStrip.displayName = "StatsStrip";

export const StatCard = styled.div`
  background: var(--c-surface);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  padding: 1.4rem 1rem;
  text-align: center;
  box-shadow: var(--s-sm);
  transition: transform var(--t), box-shadow var(--t);

  &:hover { transform: translateY(-3px); box-shadow: var(--s-lg); }
`;
StatCard.displayName = "StatCard";

export const StatNumber = styled.div`
  font-size: clamp(1.6rem, 4vw, 2.2rem);
  font-weight: 900;
  background: linear-gradient(135deg, var(--c-accent), var(--c-accent-2));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.1;
  margin-bottom: 0.3rem;
`;
StatNumber.displayName = "StatNumber";

export const StatLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--c-text);
  opacity: 0.58;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;
StatLabel.displayName = "StatLabel";

// ─── Feature cards grid ───────────────────────────────────────────────────────

export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.4rem;

  @media (min-width: 36em) { grid-template-columns: repeat(2, 1fr); }
  @media (min-width: 64em) { grid-template-columns: repeat(3, 1fr); }
  @media (min-width: 80em) { grid-template-columns: repeat(4, 1fr); }
`;
CardsGrid.displayName = "CardsGrid";

// ─── Feature card ─────────────────────────────────────────────────────────────

export const FeatureCard = styled.article`
  position: relative;
  background: var(--c-surface);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid var(--c-border);
  border-radius: var(--r-xl);
  padding: 2rem 1.6rem 2.2rem;
  box-shadow: var(--s-sm);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  transition: transform var(--t-slow), box-shadow var(--t-slow),
              border-color var(--t), background var(--t);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      105deg,
      transparent 40%,
      rgba(255,255,255,0.55) 50%,
      transparent 60%
    );
    background-size: 200% 100%;
    background-position: -200% 0;
    transition: background-position 0.55s ease;
    pointer-events: none;
    border-radius: inherit;
  }

  &::after {
    content: "";
    position: absolute;
    top: 1.5rem; bottom: 1.5rem; left: 0;
    width: 3px;
    background: linear-gradient(180deg, var(--c-accent), var(--c-accent-2));
    border-radius: 0 3px 3px 0;
    opacity: 0;
    transition: opacity var(--t);
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: var(--s-xl);
    border-color: var(--c-border-accent);
    background: var(--c-surface-hi);
    &::before { background-position: 200% 0; }
    &::after  { opacity: 1; }
  }
  &:active { transform: translateY(-2px); }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFadeUp} 0.5s ease both;
    animation-delay: ${({ $index }) => Math.min($index * 100, 500)}ms;
  }
`;
FeatureCard.displayName = "FeatureCard";

// ─── Icon container ───────────────────────────────────────────────────────────

export const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: var(--r);
  background: linear-gradient(135deg, var(--c-accent), var(--c-accent-2));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.2rem;
  flex-shrink: 0;
  box-shadow: 0 6px 18px ${COLORS.primaryGlow};
  font-size: 1.5rem;
  transition: transform var(--t), box-shadow var(--t);

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFloat} ${({ $delay }) => 3.5 + $delay * 0.6}s ease-in-out infinite;
  }

  ${FeatureCard}:hover & {
    transform: scale(1.1) rotate(-4deg);
    box-shadow: 0 8px 28px ${COLORS.primaryGlow};
  }
`;
IconWrap.displayName = "IconWrap";

export const FeatureImg = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  display: block;
`;
FeatureImg.displayName = "FeatureImg";

export const FeatureEmoji = styled.span`
  font-size: 1.65rem;
  line-height: 1;
  display: block;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.18));
`;
FeatureEmoji.displayName = "FeatureEmoji";

// ─── Card content ─────────────────────────────────────────────────────────────

export const FeatureTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--c-text);
  margin: 0 0 0.65rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
`;
FeatureTitle.displayName = "FeatureTitle";

export const FeatureText = styled.p`
  font-size: 0.85rem;
  color: var(--c-text);
  opacity: 0.68;
  line-height: 1.75;
  margin: 0;
  flex: 1;
`;
FeatureText.displayName = "FeatureText";

export const CardLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--c-accent);
  margin-top: 1.2rem;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity var(--t), transform var(--t);

  ${FeatureCard}:hover & {
    opacity: 1;
    transform: translateY(0);
  }

  svg { width: 12px; height: 12px; transition: transform var(--t); }
  &:hover svg { transform: translateX(3px); }
`;
CardLink.displayName = "CardLink";

// ─── Testimonial quote band ───────────────────────────────────────────────────

export const QuoteBand = styled.blockquote`
  margin: 4.5rem 0 0;
  background: linear-gradient(
    135deg,
    ${COLORS.primary}    0%,
    ${COLORS.primaryDark} 60%,
    ${COLORS.accentDark}  100%
  );
  border-radius: var(--r-xl);
  padding: 3rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 52px ${COLORS.primaryGlow};

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kScaleIn} 0.65s ease both 0.25s;
  }
  @media (min-width: 48em) { padding: 4rem 4rem; }
  @media (min-width: 64em) { padding: 4.5rem 8rem; }
`;
QuoteBand.displayName = "QuoteBand";

export const QuoteOrb = styled.span`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;

  &.o1 { width: 320px; height: 320px; top: -120px; right: -80px;  background: rgba(255,255,255,0.07); }
  &.o2 { width: 220px; height: 220px; bottom: -80px; left: -60px; background: rgba(255,255,255,0.05); }
`;
QuoteOrb.displayName = "QuoteOrb";

export const QuoteGlyph = styled.div`
  font-size: 5rem;
  line-height: 1;
  color: rgba(255,255,255,0.18);
  font-family: Georgia, serif;
  margin-bottom: -1rem;
  user-select: none;
`;
QuoteGlyph.displayName = "QuoteGlyph";

export const QuoteText = styled.p`
  font-size: clamp(1rem, 2.8vw, 1.3rem);
  font-weight: 600;
  color: rgba(255,255,255,0.95);
  line-height: 1.72;
  margin: 0 0 2rem;
  font-style: italic;
  position: relative;
  z-index: 1;
`;
QuoteText.displayName = "QuoteText";

export const QuoteCta = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.85rem 2.2rem;
  border-radius: 999px;
  border: 2px solid rgba(253,250,246,0.55);
  background: rgba(253,250,246,0.14);
  color: ${COLORS.white};
  font-size: 0.92rem;
  font-weight: 800;
  cursor: pointer;
  min-height: 50px;
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
  transition: background var(--t), border-color var(--t),
              transform var(--t), box-shadow var(--t);

  svg { width: 16px; height: 16px; transition: transform var(--t); }

  &:hover {
    background: rgba(253,250,246,0.26);
    border-color: rgba(253,250,246,0.88);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0,0,0,0.2);
  }
  &:hover svg { transform: translateX(4px); }
  &:active { transform: translateY(0) scale(0.97); }
  &:focus-visible { outline: 2px solid ${COLORS.white}; outline-offset: 3px; }
`;
QuoteCta.displayName = "QuoteCta";